/**
 * Filter State Context
 *
 * Manages search, filtering, sorting, and pagination state
 * Separated from main AppContext for better performance and organization
 */

import React, { createContext, useContext, useReducer, type ReactNode } from 'react'
import type { CPUFilters, CPUSortOptions } from '../models/cpu/cpu-model'
import type { MotherboardFilters } from '../models/motherboard/motherboard-model'

/**
 * Filter state interface
 */
export interface FilterStateState {
  // Search and Filtering
  searchTerm: string
  filters: CPUFilters
  motherboardFilters: MotherboardFilters
  sortConfig: CPUSortOptions

  // Pagination
  currentPage: number
  pageSize: number

  // View state
  viewMode: 'grid' | 'list' | 'table'

  // Cache for search results
  searchCache: Map<string, unknown[]>
}

/**
 * Filter state action types
 */
export type FilterStateAction =
  // Search Actions
  | { type: 'SET_SEARCH_TERM'; payload: string }
  | { type: 'CLEAR_SEARCH' }

  // Filter Actions
  | { type: 'SET_CPU_FILTERS'; payload: CPUFilters }
  | { type: 'UPDATE_CPU_FILTERS'; payload: Partial<CPUFilters> }
  | { type: 'CLEAR_CPU_FILTERS' }
  | { type: 'SET_MOTHERBOARD_FILTERS'; payload: MotherboardFilters }
  | { type: 'UPDATE_MOTHERBOARD_FILTERS'; payload: Partial<MotherboardFilters> }
  | { type: 'CLEAR_MOTHERBOARD_FILTERS' }

  // Sort Actions
  | { type: 'SET_SORT_CONFIG'; payload: CPUSortOptions }
  | { type: 'UPDATE_SORT_CONFIG'; payload: Partial<CPUSortOptions> }
  | { type: 'CLEAR_SORT' }

  // Pagination Actions
  | { type: 'SET_CURRENT_PAGE'; payload: number }
  | { type: 'SET_PAGE_SIZE'; payload: number }
  | { type: 'RESET_PAGINATION' }

  // View Actions
  | { type: 'SET_VIEW_MODE'; payload: 'grid' | 'list' | 'table' }

  // Cache Actions
  | { type: 'ADD_TO_SEARCH_CACHE'; payload: { key: string; results: unknown[] } }
  | { type: 'CLEAR_SEARCH_CACHE' }
  | { type: 'REMOVE_FROM_SEARCH_CACHE'; payload: string }

  // Reset Actions
  | { type: 'RESET_ALL_FILTERS' }

/**
 * Default CPU filters
 * Note: With exactOptionalPropertyTypes, we omit properties rather than setting undefined
 */
const defaultCPUFilters: CPUFilters = {
  manufacturer: [],
  coreCount: []
}

/**
 * Default motherboard filters
 * Note: With exactOptionalPropertyTypes, we omit properties rather than setting undefined
 */
const defaultMotherboardFilters: MotherboardFilters = {
  manufacturer: [],
  chipset: [],
  cpuSocket: [],
  formFactor: [],
  memoryType: []
}

/**
 * Default sort configuration
 */
const defaultSortConfig: CPUSortOptions = {
  field: 'model',
  order: 'asc'
}

/**
 * Initial filter state
 */
const initialFilterState: FilterStateState = {
  searchTerm: '',
  filters: defaultCPUFilters,
  motherboardFilters: defaultMotherboardFilters,
  sortConfig: defaultSortConfig,
  currentPage: 1,
  pageSize: 25,
  viewMode: 'table',
  searchCache: new Map()
}

/**
 * Filter state reducer
 */
function filterStateReducer(state: FilterStateState, action: FilterStateAction): FilterStateState {
  switch (action.type) {
    // Search Cases
    case 'SET_SEARCH_TERM':
      return {
        ...state,
        searchTerm: action.payload,
        currentPage: 1 // Reset pagination when searching
      }

    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchTerm: '',
        currentPage: 1
      }

    // CPU Filter Cases
    case 'SET_CPU_FILTERS':
      return {
        ...state,
        filters: action.payload,
        currentPage: 1 // Reset pagination when filtering
      }

    case 'UPDATE_CPU_FILTERS':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload
        },
        currentPage: 1
      }

    case 'CLEAR_CPU_FILTERS':
      return {
        ...state,
        filters: defaultCPUFilters,
        currentPage: 1
      }

    // Motherboard Filter Cases
    case 'SET_MOTHERBOARD_FILTERS':
      return {
        ...state,
        motherboardFilters: action.payload,
        currentPage: 1
      }

    case 'UPDATE_MOTHERBOARD_FILTERS':
      return {
        ...state,
        motherboardFilters: {
          ...state.motherboardFilters,
          ...action.payload
        },
        currentPage: 1
      }

    case 'CLEAR_MOTHERBOARD_FILTERS':
      return {
        ...state,
        motherboardFilters: defaultMotherboardFilters,
        currentPage: 1
      }

    // Sort Cases
    case 'SET_SORT_CONFIG':
      return {
        ...state,
        sortConfig: action.payload,
        currentPage: 1
      }

    case 'UPDATE_SORT_CONFIG':
      return {
        ...state,
        sortConfig: {
          ...state.sortConfig,
          ...action.payload
        },
        currentPage: 1
      }

    case 'CLEAR_SORT':
      return {
        ...state,
        sortConfig: defaultSortConfig,
        currentPage: 1
      }

    // Pagination Cases
    case 'SET_CURRENT_PAGE':
      return {
        ...state,
        currentPage: Math.max(1, action.payload)
      }

    case 'SET_PAGE_SIZE': {
      const newPageSize = Math.max(5, Math.min(100, action.payload))
      return {
        ...state,
        pageSize: newPageSize,
        currentPage: 1 // Reset to first page when changing page size
      }
    }

    case 'RESET_PAGINATION':
      return {
        ...state,
        currentPage: 1
      }

    // View Cases
    case 'SET_VIEW_MODE':
      return {
        ...state,
        viewMode: action.payload
      }

    // Cache Cases
    case 'ADD_TO_SEARCH_CACHE': {
      const newCache = new Map(state.searchCache)
      newCache.set(action.payload.key, action.payload.results)

      // Limit cache size to prevent memory issues
      if (newCache.size > 50) {
        const firstKey = newCache.keys().next().value
        if (firstKey !== undefined) {
          newCache.delete(firstKey)
        }
      }

      return {
        ...state,
        searchCache: newCache
      }
    }

    case 'CLEAR_SEARCH_CACHE':
      return {
        ...state,
        searchCache: new Map()
      }

    case 'REMOVE_FROM_SEARCH_CACHE': {
      const updatedCache = new Map(state.searchCache)
      updatedCache.delete(action.payload)
      return {
        ...state,
        searchCache: updatedCache
      }
    }

    // Reset Cases
    case 'RESET_ALL_FILTERS':
      return {
        ...state,
        searchTerm: '',
        filters: defaultCPUFilters,
        motherboardFilters: defaultMotherboardFilters,
        sortConfig: defaultSortConfig,
        currentPage: 1,
        searchCache: new Map()
      }

    default:
      return state
  }
}

/**
 * Filter state context interface
 */
interface FilterStateContextType {
  state: FilterStateState
  dispatch: React.Dispatch<FilterStateAction>

  // Utility functions
  setSearchTerm: (term: string) => void
  clearSearch: () => void
  updateCPUFilters: (filters: Partial<CPUFilters>) => void
  clearCPUFilters: () => void
  updateMotherboardFilters: (filters: Partial<MotherboardFilters>) => void
  clearMotherboardFilters: () => void
  updateSort: (sort: Partial<CPUSortOptions>) => void
  setCurrentPage: (page: number) => void
  setPageSize: (size: number) => void
  setViewMode: (mode: 'grid' | 'list' | 'table') => void
  resetAllFilters: () => void

  // Cache utilities
  getCachedResults: (key: string) => unknown[] | undefined
  setCachedResults: (key: string, results: unknown[]) => void
  generateCacheKey: (searchTerm: string, filters: unknown, sort: unknown) => string
}

/**
 * Filter state context
 */
const FilterStateContext = createContext<FilterStateContextType | undefined>(undefined)

/**
 * Filter state provider component
 */
export function FilterStateProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(filterStateReducer, initialFilterState)

  /**
   * Set search term
   */
  const setSearchTerm = (term: string) => {
    dispatch({ type: 'SET_SEARCH_TERM', payload: term })
  }

  /**
   * Clear search
   */
  const clearSearch = () => {
    dispatch({ type: 'CLEAR_SEARCH' })
  }

  /**
   * Update CPU filters
   */
  const updateCPUFilters = (filters: Partial<CPUFilters>) => {
    dispatch({ type: 'UPDATE_CPU_FILTERS', payload: filters })
  }

  /**
   * Clear CPU filters
   */
  const clearCPUFilters = () => {
    dispatch({ type: 'CLEAR_CPU_FILTERS' })
  }

  /**
   * Update motherboard filters
   */
  const updateMotherboardFilters = (filters: Partial<MotherboardFilters>) => {
    dispatch({ type: 'UPDATE_MOTHERBOARD_FILTERS', payload: filters })
  }

  /**
   * Clear motherboard filters
   */
  const clearMotherboardFilters = () => {
    dispatch({ type: 'CLEAR_MOTHERBOARD_FILTERS' })
  }

  /**
   * Update sort configuration
   */
  const updateSort = (sort: Partial<CPUSortOptions>) => {
    dispatch({ type: 'UPDATE_SORT_CONFIG', payload: sort })
  }

  /**
   * Set current page
   */
  const setCurrentPage = (page: number) => {
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page })
  }

  /**
   * Set page size
   */
  const setPageSize = (size: number) => {
    dispatch({ type: 'SET_PAGE_SIZE', payload: size })
  }

  /**
   * Set view mode
   */
  const setViewMode = (mode: 'grid' | 'list' | 'table') => {
    dispatch({ type: 'SET_VIEW_MODE', payload: mode })
  }

  /**
   * Reset all filters
   */
  const resetAllFilters = () => {
    dispatch({ type: 'RESET_ALL_FILTERS' })
  }

  /**
   * Get cached search results
   */
  const getCachedResults = (key: string) => {
    return state.searchCache.get(key)
  }

  /**
   * Set cached search results
   */
  const setCachedResults = (key: string, results: unknown[]) => {
    dispatch({ type: 'ADD_TO_SEARCH_CACHE', payload: { key, results } })
  }

  /**
   * Generate cache key for search/filter/sort combination
   */
  const generateCacheKey = (searchTerm: string, filters: unknown, sort: unknown) => {
    return JSON.stringify({ searchTerm, filters, sort })
  }

  const contextValue: FilterStateContextType = {
    state,
    dispatch,
    setSearchTerm,
    clearSearch,
    updateCPUFilters,
    clearCPUFilters,
    updateMotherboardFilters,
    clearMotherboardFilters,
    updateSort,
    setCurrentPage,
    setPageSize,
    setViewMode,
    resetAllFilters,
    getCachedResults,
    setCachedResults,
    generateCacheKey
  }

  return (
    <FilterStateContext.Provider value={contextValue}>
      {children}
    </FilterStateContext.Provider>
  )
}

/**
 * Hook to use filter state context
 */
export function useFilterState() {
  const context = useContext(FilterStateContext)
  if (context === undefined) {
    throw new Error('useFilterState must be used within a FilterStateProvider')
  }
  return context
}